import { getCurrentUserName } from './auth';
import { resolveBranchForPrint } from './branchPrint';
import { resolvePhotoUrl } from './photoUrl';

const fmt = (v) =>
  parseFloat(v || 0).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' });

export const printRentalAgreement = (rental = {}, template = null) => {
  const preparedBy = getCurrentUserName();
  const win = window.open('', '_blank', 'width=900,height=800,scrollbars=yes,resizable=yes');
  if (!win) {
    alert('Please allow pop-ups to view or print the rental agreement');
    return;
  }

  const baseUrl = window.location.origin;
  const logoUrl = `${baseUrl}/LOGOFile.png`;
  const items = rental.items || [];
  const cycle = ['daily', 'weekly', 'monthly'].includes(String(rental.billing_cycle || '').toLowerCase())
    ? String(rental.billing_cycle).toLowerCase()
    : 'monthly';
  const cycleLabel = cycle === 'daily' ? 'day' : cycle === 'weekly' ? 'week' : 'month';
  const cycleTitle = cycle === 'daily' ? 'Daily' : cycle === 'weekly' ? 'Weekly' : 'Monthly';
  const invoiceEach = cycle === 'daily' ? 'each day' : cycle === 'weekly' ? 'each week' : 'each month';
  const branch = resolveBranchForPrint({
    branchName: rental.branch_name || rental.branch?.name,
    branchCode: rental.branch_code || rental.branch?.branch_code,
    branch: rental.branch
  });
  const templateUrl = template?.url ? resolvePhotoUrl(template.url) : '';
  const signedUrl = rental.agreement_url ? resolvePhotoUrl(rental.agreement_url) : '';
  const isPdf = (url, mime) =>
    String(mime || '').includes('pdf') || String(url || '').toLowerCase().includes('.pdf');

  const itemsHtml = items.map((item) => `
    <tr>
      <td>${item.product_name || '-'}</td>
      <td>${(item.serials || []).join(', ') || item.serial_number || '—'}</td>
      <td>${item.quantity || 1}</td>
      <td class="right">${fmt(item.period_rate)}</td>
    </tr>
  `).join('');

  const embedBlock = (title, url, mime) => {
    if (!url) return '';
    if (isPdf(url, mime)) {
      return `<div class="embed-block"><h3>${title}</h3><iframe src="${url}" class="pdf-frame"></iframe></div>`;
    }
    return `<div class="embed-block"><h3>${title}</h3><img src="${url}" class="agree-img" alt="${title}"></div>`;
  };

  win.document.write(`
<!DOCTYPE html>
<html>
<head>
  <title>Rental Agreement ${rental.rental_number || ''}</title>
  <base href="${baseUrl}/">
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    @media print {
      @page { size: A4; margin: 0.8cm; }
      .no-print { display: none !important; }
      .pdf-frame { height: 240mm; }
    }
    body {
      font-family: 'Poppins', sans-serif;
      font-size: 12px;
      padding: 12px;
      max-width: 210mm;
      margin: 0 auto;
      color: #222;
      line-height: 1.45;
    }
    .header {
      display: flex; align-items: center; justify-content: space-between;
      margin-bottom: 14px; padding-bottom: 8px; border-bottom: 2px solid #1976d2;
    }
    .company-logo { height: 90px; width: auto; max-width: 220px; object-fit: contain; }
    .company-info { flex: 1; text-align: right; }
    .company-name { font-size: 15px; font-weight: 700; color: #1976d2; }
    h1 { font-size: 18px; text-align: center; margin: 12px 0; color: #1976d2; }
    h2 { font-size: 13px; margin: 14px 0 6px; color: #1565c0; }
    table { width: 100%; border-collapse: collapse; margin: 8px 0; }
    th { background: #1976d2; color: white; padding: 7px 6px; text-align: left; }
    td { padding: 6px; border-bottom: 1px solid #eee; }
    .right { text-align: right; }
    .box { background: #f7f9fc; border-left: 3px solid #1976d2; padding: 8px 10px; margin: 8px 0; }
    .terms li { margin: 4px 0 4px 18px; }
    .sigs { display: flex; justify-content: space-between; margin-top: 36px; }
    .sig { width: 45%; text-align: center; }
    .print-button {
      position: fixed; bottom: 20px; right: 20px; background: #1976d2; color: white;
      border: none; padding: 10px 20px; border-radius: 4px; font-size: 13px; cursor: pointer; font-weight: 600;
    }
    .embed-block { margin-top: 18px; page-break-before: always; }
    .pdf-frame { width: 100%; height: 900px; border: 1px solid #ddd; }
    .agree-img { max-width: 100%; border: 1px solid #ddd; }
  </style>
</head>
<body>
  <div class="header">
    <div><img src="${logoUrl}" alt="Logo" class="company-logo" onerror="this.style.display='none'"></div>
    <div class="company-info">
      <div class="company-name">Sun Systems International</div>
      <div>1118, Pannipitiya Road, Wickramasinghepura, Thalawathugoda<br>
      Tel: 011 27 96 579 | 94 77 987 1130<br>Branch: ${branch.label}</div>
    </div>
  </div>

  <h1>RENTAL AGREEMENT</h1>
  <p style="text-align:center;margin-bottom:12px;"><strong>${rental.rental_number || ''}</strong></p>

  <div class="box">
    This agreement is made between <strong>Sun Systems International</strong> (“Lessor”) and
    <strong>${rental.customer?.name || 'the Customer'}</strong>
    ${rental.customer?.phone ? `(${rental.customer.phone})` : ''}
    (“Lessee”) for the rental of the equipment listed below.
  </div>

  <h2>1. Equipment</h2>
  <table>
    <thead>
      <tr><th>Product</th><th>Serial</th><th>Qty</th><th class="right">Rate / ${cycleLabel}</th></tr>
    </thead>
    <tbody>${itemsHtml || '<tr><td colspan="4">No items</td></tr>'}</tbody>
  </table>

  <h2>2. Rental period & charges</h2>
  <table>
    <tr><td><strong>Billing</strong></td><td>${cycleTitle} × ${rental.duration_count || 1}</td></tr>
    <tr><td><strong>Period</strong></td><td>${rental.start_date || rental.rental_date || '-'} → ${rental.end_date || rental.return_date || '-'}</td></tr>
    <tr><td><strong>Period amount</strong></td><td>${fmt((rental.items || []).reduce((s, it) => s + (parseFloat(it.period_rate || 0) * (it.quantity || 1)), 0))}</td></tr>
    <tr><td><strong>Deposit</strong></td><td>${fmt(rental.deposit)}</td></tr>
  </table>

  <h2>3. Terms</h2>
  <ol class="terms">
    <li>The Lessee shall keep the equipment in good working condition and use it only for intended purposes.</li>
    <li>Invoices are generated ${invoiceEach} for the rental period. Payment is due on the invoice due date.</li>
    <li>The security deposit may be applied against unpaid charges, damage, or missing accessories.</li>
    <li>Loss, theft, or damage beyond normal wear is the Lessee’s responsibility at replacement value.</li>
    <li>Serial-numbered items remain the property of the Lessor and must be returned with the same serials.</li>
    <li>Early return does not automatically cancel invoices already issued unless agreed in writing.</li>
    <li>This agreement covers rental ${rental.rental_number || ''} and any extension of the same contract.</li>
    ${rental.notes ? `<li>Special notes: ${String(rental.notes).replace(/</g, '&lt;')}</li>` : ''}
  </ol>

  <div class="sigs">
    <div class="sig">..........................................................<br><strong>Lessor / Authorized</strong><br>${preparedBy}</div>
    <div class="sig">..........................................................<br><strong>Lessee / Customer</strong><br>${rental.customer?.name || ''}</div>
  </div>

  ${embedBlock('Company rental agreement (uploaded template)', templateUrl, template?.mime)}
  ${embedBlock('Signed agreement for this rental', signedUrl, '')}

  <button class="print-button no-print" onclick="window.print();">Print / Save as PDF</button>
</body>
</html>
  `);

  win.document.close();
  win.focus();
};

export default printRentalAgreement;
